import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import i18n from '../../utilities/i18n';
import mediator from '../../utilities/mediator';
import * as Yup from 'yup';
import TabInfo from './studyWizardComponents/TabInfo';
import {
  Button,
  Card,
  HStack,
  TutorialScreen,
  VStack,
  Text,
  ModalDesktop,
  ProgressIndicator,
} from '@adc/parallax-component-library';
import {
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  setNestedObjectValues,
} from 'formik';
import { Site, Study, Subject } from '../../models';
import TabSites from './studyWizardComponents/TabSites';
import { useAppSelector } from '../../reducers/hooks';
import TabSubjects from './studyWizardComponents/TabSubjects';
import TabReview from './studyWizardComponents/TabReview';
import MessageBanner from '../messageBanner/MessageBanner';
import { studyIdSchema } from 'adc-sirius-module-common';

const steps = ['info', 'sites', 'subjects', 'review'];

export type StudyCreateFormValues = {
  sites: Site[];
  siteCount: number; // used to generic temporary IDs for new sites
  subjects: Subject[];
  subjectCount: number; // used to generic temporary IDs for new subjects
  errorMessage: string | null;
} & Pick<Study, 'name' | 'description' | 'region' | 'organizationKey' | 'type'>;

export default function StudyCreate() {
  const navigate = useNavigate();

  const organizationKey = useAppSelector(
    (state) => state.organizations.currentOrgKey,
  );

  const initialValues: StudyCreateFormValues = {
    name: '',
    description: '',
    region: '',
    type: '',
    organizationKey,
    sites: [],
    siteCount: 0,
    subjects: [],
    subjectCount: 0,
    errorMessage: null,
  };

  const [currentIndex, setCurrentIndex] = useState(0);

  const schema = Yup.object({
    name: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .matches(
        studyIdSchema.matcher,
        i18n.t('Form.allowedSpecialCharacters', {
          characters: studyIdSchema.allowedSpecialCharacters.join(' '),
        }),
      )
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', {
            count: studyIdSchema.maxLength,
          }),
        (val) =>
          studyIdSchema.maxLength && val
            ? val.length < studyIdSchema.maxLength
            : true,
      ),
    description: Yup.string().optional(),
    type: Yup.string().optional(),
    region: Yup.string().optional(),
    sites: Yup.array(),
    subjects: Yup.array(),
    devices: Yup.array(),
    errorMessage: Yup.string().nullable(),
  });

  const translatedSteps = steps.map((step) =>
    i18n.t(`Page_StudyCreate.tab_${step}`),
  );
  const onSubmit = (
    values: StudyCreateFormValues,
    helpers: FormikHelpers<StudyCreateFormValues>,
  ) => {
    let studyKey;

    helpers.setSubmitting(true);

    // create study
    mediator
      .request<Study>('study:createStudy', {
        data: {
          name: values.name,
          description: values.description,
          region: values.region,
          type: values.type,
          organizationKey: organizationKey,
        },
      })
      .then((createdStudy) => {
        studyKey = createdStudy.key;
        const siteCreationPromises: Promise<Subject[]>[] = [];

        values.sites.forEach((site) => {
          const subjects = values.subjects.filter(
            (subject) => subject.siteKey === site.key,
          );

          siteCreationPromises.push(
            // create site
            mediator
              .request<Site>('site:createSite', {
                data: {
                  ...site,
                  studyKey: createdStudy.key,
                },
              })
              .then((createdSite) => {
                // update site's subjects with created site ID
                subjects.forEach(
                  (subject) => (subject.siteKey = createdSite.key),
                );

                return subjects;
              }),
          );
        });
        return Promise.all(siteCreationPromises);
      })
      .then((result) => {
        const subjectCreationPromises: Promise<Subject>[] = [];

        // subjects and devices are grouped by site
        result.forEach((subjects) => {
          subjects.forEach((subject) => {
            subjectCreationPromises.push(
              // create subjects
              mediator.request<Subject>('subject:createSubject', {
                data: {
                  ...subject,
                  key: undefined,
                },
              }),
            );
          });
        });

        return Promise.all(subjectCreationPromises);
      })
      .then(() => {
        helpers.setErrors({});
        navigate(`/studies/${studyKey}`);
      })
      .catch((error) => {
        console.error('Error creating study:', error);

        let errorMessage;

        if (error.status === 409) {
          errorMessage = i18n.t('Page_StudyCreate.creation_error_duplicate');
        } else {
          errorMessage = i18n.t('Page_StudyCreate.creation_error_generic');
        }

        helpers.setErrors({
          errorMessage,
        });
      })
      .finally(() => {
        helpers.setSubmitting(false);
      });
  };

  const renderFooter = useCallback(
    (formikProps: FormikProps<StudyCreateFormValues>) => {
      const isFirstStep = currentIndex === 0;
      const isLastStep = currentIndex === steps.length - 1;

      const goForward = (formikProps: FormikProps<StudyCreateFormValues>) => {
        if (currentIndex < steps.length - 1) {
          formikProps.validateForm().then((stepErrors) => {
            const isValid = Object.keys(stepErrors).length === 0;

            formikProps.setTouched(setNestedObjectValues(stepErrors, true));

            if (isValid) {
              setCurrentIndex(currentIndex + 1);
            }
          });
        }
      };
      const goToReview = () => {
        setCurrentIndex(steps.length - 1);
      };
      const goBack = (formikProps: FormikProps<StudyCreateFormValues>) => {
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);

          if (formikProps.errors.errorMessage) {
            formikProps.setErrors({});
          }
        }
      };
      const cancelCreation = (isDirty: boolean) => {
        if (isDirty) {
          ModalDesktop.show({
            title: i18n.t('Page_StudyCreate.confirm_cancel_title'),
            details: i18n.t('Page_StudyCreate.confirm_cancel_message'),
            buttons: [
              {
                label: i18n.t('Common.stay'),
                variant: 'secondary',
                onPress: () => {
                  ModalDesktop.hide();
                },
              },
              {
                label: i18n.t('Common.leave'),
                variant: 'primary',
                onPress: () => {
                  ModalDesktop.hide();
                  navigate('/');
                },
              },
            ],
          });
        } else {
          navigate('/');
        }
      };

      return (
        <HStack justifyContent="space-between">
          <Button
            label={i18n.t('Common.cancel')}
            onPress={() => cancelCreation(formikProps.dirty)}
            variant="secondary"
            testID="cancel"
          />

          <HStack gap="$2">
            {!isLastStep && !isFirstStep ? (
              <Button
                label={i18n.t('Page_StudyCreate.skip_to_review')}
                onPress={() => goToReview()}
                variant="tertiary"
                testID="skip-to-review"
              />
            ) : null}
            <Button
              label={i18n.t('Common.back')}
              onPress={() => goBack(formikProps)}
              disabled={isFirstStep}
              variant="secondary"
              testID="back"
            />
            {isLastStep ? (
              <Button
                label={i18n.t('Common.submit')}
                onPress={formikProps.submitForm}
                disabled={formikProps.isSubmitting}
                testID="submit"
              />
            ) : (
              <Button
                label={i18n.t('Common.next')}
                onPress={() => goForward(formikProps)}
                disabled={formikProps.isValidating}
                testID="next"
              />
            )}
          </HStack>
        </HStack>
      );
    },
    [currentIndex, navigate],
  );

  return (
    <VStack height={'100%'}>
      <Card>
        <VStack gap={'$4'} height={'100%'}>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={schema}
          >
            {(formikProps) => (
              <Form id="study-create-form">
                <VStack gap="$4">
                  <Text type="$h2.strong" textAlign="left">
                    {i18n.t('Page_StudyCreate.wizard_title')}
                  </Text>
                  <ProgressIndicator
                    steps={translatedSteps}
                    progress={currentIndex + 1} // Component uses 1-based index
                  />
                </VStack>
                {formikProps.errors?.errorMessage ? (
                  <VStack paddingVertical="$4">
                    <MessageBanner
                      type="danger"
                      headerText={i18n.t(
                        'Page_StudyCreate.creation_error_header',
                      )}
                      iconSize="M"
                      bodyText={i18n.t(formikProps.errors.errorMessage) || ''}
                      testID="error-banner"
                    />
                  </VStack>
                ) : null}
                <TutorialScreen index={currentIndex}>
                  <TutorialScreen.Page backgroundColor="$neutral.0">
                    <TutorialScreen.Content
                      illustration={<TabInfo />}
                      justifyIllustration="center"
                    />
                    <TutorialScreen.Footer>
                      {renderFooter(formikProps)}
                    </TutorialScreen.Footer>
                  </TutorialScreen.Page>
                  <TutorialScreen.Page>
                    <TutorialScreen.Content
                      illustration={<TabSites />}
                      justifyIllustration="center"
                    />
                  </TutorialScreen.Page>
                  <TutorialScreen.Page>
                    <TutorialScreen.Content
                      illustration={<TabSubjects />}
                      justifyIllustration="center"
                    />
                  </TutorialScreen.Page>
                  <TutorialScreen.Page>
                    <TutorialScreen.Content
                      illustration={<TabReview />}
                      justifyIllustration="center"
                    />
                  </TutorialScreen.Page>
                </TutorialScreen>
              </Form>
            )}
          </Formik>
        </VStack>
      </Card>
    </VStack>
  );
}
